import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Router } from '@uhg-abyss/web/ui/Router';
import { Text } from '@uhg-abyss/web/ui/Text';

const Page = styled('div', {
  width: '100%',
  display: 'grid',
  gap: '1rem',
  paddingBottom: '1.5rem',
});

const InfoPanel = styled(Card, {
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #c8d8e6',
  backgroundColor: '$white',
  padding: '1.25rem',
  boxSizing: 'border-box',
});

const HelpHeaderPanel = styled(InfoPanel, {
  padding: '0.875rem 1rem',
});

const Section = styled('section', {
  display: 'grid',
  gap: '0.75rem',
  scrollMarginTop: '5rem',
});

const SectionList = styled('ul', {
  margin: '0.25rem 0 0.5rem 1.25rem',
  padding: 0,
  display: 'grid',
  gap: '0.4rem',
  listStyleType: 'disc',
  listStylePosition: 'outside',
  '& > li': {
    paddingLeft: '0.25rem',
    '& > h6': {
      fontSize: '1.25rem',
      lineHeight: 1.4,
      margin: '0 0 0.75rem',
    },
    '& > p': {
      margin: '0 0 1.25rem',
    },
  },
});

const NumberList = styled('ol', {
  margin: '0.25rem 0 0.5rem 1.25rem',
  padding: 0,
  display: 'grid',
  gap: '0.4rem',
  listStyleType: 'decimal',
  listStylePosition: 'outside',
  '& > li': {
    paddingLeft: '0.25rem',
  },
});

const NavList = styled('ul', {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  display: 'flex',
  flexWrap: 'nowrap',
  justifyContent: 'flex-start',
  gap: '0.375rem',
});

const NavLink = styled('a', {
  display: 'inline-block',
  padding: '0.375rem 0.625rem',
  borderRadius: '8px',
  border: '1px solid #bed0e0',
  color: '#0b2f4b',
  textDecoration: 'none',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#eef4fa',
  },
});

const BackToTopLink = styled('a', {
  position: 'sticky',
  bottom: '1rem',
  justifySelf: 'end',
  alignSelf: 'end',
  margin: '0 1rem 1rem 0',
  color: '#0b2f4b',
  fontWeight: 600,
  backgroundColor: '#d9e8f5',
  border: '1px solid #bed0e0',
  borderRadius: '8px',
  padding: '0.375rem 0.625rem',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
  textDecoration: 'none',
  zIndex: 1,
  '&:hover': {
    backgroundColor: '#eef4fa',
  },
});

const SupportImage = styled('img', {
  display: 'block',
  width: '100%',
  height: 'auto',
  maxWidth: '700px',
  border: '1px solid #d4dce3',
  borderRadius: '10px',
});

const QuestionSupportImage = styled(SupportImage, {
  width: 'calc(100% - 0.5rem)',
  marginLeft: '0.5rem',
  borderRadius: 0,
});

const ImageCaption = styled('div', {
  display: 'block',
  fontWeight: 700,
});

const troubleshootingImageWidths = {
  eesToUhcesHome: '500px',
  uhcesRfiCard: '150px',
  uhcesToEesScreen: '550px',
  eesRfiCard: '450px',
} as const;

const SectionTitle = ({ children }: { children: React.ReactNode }) => {return (
  <Heading
    level={2}
    size="md"
    textAlign="left"
    css={{ borderBottom: '1px solid #ccc', paddingBottom: '0.3125rem' }}
  >
    {children}
  </Heading>
)};

const BackToTop = () => {return <BackToTopLink href="#rfi-help-top">Back to top</BackToTopLink>};

const scrollToHashTarget = () => {
  const hash = window.location.hash.replace('#', '').trim();
  if (!hash) {
    return;
  }

  const element = window.document.getElementById(hash);
  if (element) {
    window.setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }
};

export const RfiInfo = () => {
  React.useEffect(() => {
    scrollToHashTarget();
    window.addEventListener('hashchange', scrollToHashTarget);

    return () => {
      window.removeEventListener('hashchange', scrollToHashTarget);
    };
  }, []);

  return (
    <React.Fragment>
      <Router.MetaTags title="RFI Help" />
      <Page>
        <HelpHeaderPanel id="rfi-help-top">
          <Heading level={1} size="md" textAlign="left">
            UHC RFI Help Guide
          </Heading>
          <NavList>
            <li>
              <NavLink href="#introduction">Introduction</NavLink>
            </li>
            <li>
              <NavLink href="#feature-highlights">Feature Highlights</NavLink>
            </li>
            <li>
              <NavLink href="#questionsupport">Question Support</NavLink>
            </li>
            <li>
              <NavLink href="#troubleshooting">Troubleshooting</NavLink>
            </li>
            <li>
              <NavLink href="#contact-support">Contact Support</NavLink>
            </li>
          </NavList>
        </HelpHeaderPanel>

        <InfoPanel>
          <Section id="introduction">
            <SectionTitle>Introduction</SectionTitle>
            <Text>
              Welcome to the UHC Request For Information (RFI) help guide. This guide will help you to understand and
              utilize the UHC RFI application.
            </Text>
          </Section>
        </InfoPanel>

        <InfoPanel>
          <Section id="feature-highlights">
            <SectionTitle>Feature Highlights</SectionTitle>
            <Text>Detailed descriptions of the main features of RFI application.</Text>

            <SectionList>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Home
                </Heading>
                <Text>
                  The 'Home' page presents you with the employer groups and medical policies associated with your
                  UserName and UserID. It is grouped by funding arrangement (e.g. Fully Insured, Self-Funded (aka
                  ASO), etc.). It shows the status of the UHC RFI (Open, In Process, Complete) and links you to the
                  UHC RFI Page where you will input your data.
                  <br />
                  <i>
                    <b>Note</b>
                  </i>
                  : The 'Home' page will only list two policies. If you want to see all policies associated with the
                  employer group/funding arrangement you will need to click the link under 'RFI Status' column to get
                  to the UHC RFI Page where all policies are displayed.
                  <br />
                  For Internal Use Only. The 'Home' page allows internal users to search by employer group name,
                  policy number, or EIN.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  UHC RFI Page
                </Heading>
                <Text>
                  The 'UHC RFI Page' is where the end-user will input their responses to the current year UHC RFI
                  questions. It displays employer group details, such as, name, EIN, and ALL policies associated with
                  the funding arrangement.
                  <br />
                  The page advances as each question is answered. Each question has a description of the data CMS wants
                  to capture and may contain helpful information and links to help you respond.
                  <br />
                  A user can partially complete the UHC RFI, save the responses, and return later to complete the UHC
                  RFI. To return to complete an UHC RFI that is in-process, click the link in the 'Employer Group
                  Name' column on the 'Home' page.
                  <br />
                  Once completed, the end-user will need to attest and submit the UHC RFI. A copy of the completed UHC
                  RFI can be downloaded for documentation and record keeping purposes.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Prior Years RFI
                </Heading>
                <Text>
                  The Prior Years RFI page provides the end-user the functionality of viewing previous years RFI
                  responses. Users can download responses for their reference.
                  <br />
                  Note: Users will have records in the Prior Years RFI page only if an UHC RFI was completed in
                  previous years.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Reports
                </Heading>
                <Text>
                  <i>
                    <b>For Internal Use Only</b>
                  </i>
                  . The 'Reports' page allows internal users to run a report of employer groups associated with a
                  specific producer (aka broker) or agent. The page displays UHC RFI status in tabular format and can
                  be downloaded to excel to be shared with the producer.
                </Text>
              </li>
            </SectionList>
          </Section>
        </InfoPanel>

        <InfoPanel>
          <Section id="questionsupport">
            <SectionTitle>Question Support</SectionTitle>
            <Text>
              What is the Average Monthly Premium (or Premium Equivalents) Paid by Members & Paid by Employers (on
              behalf of members)?
            </Text>
            <QuestionSupportImage src="/images/rfi-info/image009.png" alt="Question support example 1" />
            <QuestionSupportImage src="/images/rfi-info/image011.png" alt="Question support example 2" />
            <QuestionSupportImage src="/images/rfi-info/image013.png" alt="Question support example 3" />
            <QuestionSupportImage src="/images/rfi-info/image015.png" alt="Question support example 4" />
            <QuestionSupportImage src="/images/rfi-info/image017.png" alt="Question support example 5" />
          </Section>
        </InfoPanel>

        <InfoPanel>
          <Section id="troubleshooting">
            <SectionTitle>Troubleshooting</SectionTitle>
            <Text>Tips for resolving common issues users might encounter.</Text>

            <SectionList>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Non-UnitedHealthcare Policies
                </Heading>
                <Text>
                  This portal contains employer groups/policies with medical coverage provided by or through
                  UnitedHealthcare Insurance Company or its affiliates; or administered by United HealthCare Services,
                  Inc. or its affiliates.
                  If your Administrative Services Agreement (ASA) is with UMR, Inc. then please send an email to your
                  UMR Account Management Representative who will guide you through their separate RxDC UHC RFI process.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Optum Rx Direct
                </Heading>
                <Text>
                  This portal contains employer groups/policies with medical coverage and embeded pharmacy coverage
                  with Optum Rx, Inc.
                  This portal does not contain pharmacy coverage contracted directly with Optum Rx, Inc.
                  (non-embedded pharmacy coverage). For pharmacy coverage directly with Optum Rx, Inc. send an email
                  to ORx Transparency
                  {' '}
                  <a href="mailto:orxtransparency@optum.com">orxtransparency@optum.com</a>
                  and someone will respond with directions.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Non-Medical Stand-Alone Coverage
                </Heading>
                <Text>
                  This portal DOES NOT contain non-medical stand-alone health coverage policies such as stand-alone
                  dental or stand-alone vision. You DO NOT have to fill out the UHC RFI for non-medical stand-alone
                  health coverage.
                </Text>
              </li>
              <li>
                <Heading level={6} size="xxs" textAlign="left">
                  Wrong Employer Group Portal
                </Heading>
                <Text>
                  UnitedHealthcare has two Employer Group portals:
                  {' '}
                  <a href="https://www.uhceservices.com/en/prelogin" target="_blank" rel="noreferrer">
                    UHCeServices (UHCeS)
                  </a>
                  {' '}
                  and
                  {' '}
                  <a href="https://employereservices.optum.com/ees/prelogin/login.do" target="_blank" rel="noreferrer">
                    Employer eServices (EeS)
                  </a>
                  . UHCeS is set to replace EeS. Depending on which portal you signed in to determines what
                  information is sent to the UHC RFI portal and can be the cause of the blank 'Home' screen.
                </Text>
                <NumberList>
                  <li>
                    <Heading level={6} size="xxs" textAlign="left">
                      From EeS application
                    </Heading>
                    <Text>
                      If you signed in to EeS to get to the UHC RFI portal and see a blank 'Home' screen, go back to
                      the EeS home page, click the 'uhceservices' link. After being redirected to the UHCeS home page
                      click the UHCeS UHC RFI article card 'Go Now' link to access the UHC RFI portal.
                    </Text>

                    <ImageCaption>EeS home page to UHCeS</ImageCaption>
                    <SupportImage
                      src="/images/rfi-info/EeServices_Home.png"
                      alt="Employer eServices to UHCeServices navigation"
                      css={{ maxWidth: troubleshootingImageWidths.eesToUhcesHome }}
                    />

                    <ImageCaption>UHCeS RFI article card on the UHCeS home page (scroll down to see it)</ImageCaption>
                    <SupportImage
                      src="/images/rfi-info/RFI_card_onUHCeS_home_page.png"
                      alt="UHC RFI article card on UHCeServices home page"
                      css={{ maxWidth: troubleshootingImageWidths.uhcesRfiCard }}
                    />
                  </li>
                  <li>
                    <Heading level={6} size="xxs" textAlign="left">
                      From UHCeS application
                    </Heading>
                    <Text>
                      If you
                      {' '}
                      <i>
                        <b>signed in to UHCeS</b>
                      </i>
                      {' '}
                      to get to the UHC RFI portal and see a blank 'Home' screen, go back to the UHCeS home page,
                      click 'Other Applications', click the 'Go to Employer eServices' link. After being redirected to
                      the EeS home page click the 'Start here' link to access the RFI portal.
                    </Text>
                    <ImageCaption>UHCeS 'Other Applications' screen</ImageCaption>
                    <SupportImage
                      src="/images/rfi-info/UHCe_to_EeS.png"
                      alt="UHCeServices Other Applications navigation"
                      css={{ maxWidth: troubleshootingImageWidths.uhcesToEesScreen }}
                    />
                    <ImageCaption>UHC RFI card on EeService home page</ImageCaption>
                    <SupportImage
                      src="/images/rfi-info/RFI_card_onEeS_home_page.png"
                      alt="RFI card on Employer eServices home page"
                      css={{ maxWidth: troubleshootingImageWidths.eesRfiCard }}
                    />
                  </li>
                </NumberList>
              </li>
            </SectionList>
          </Section>
        </InfoPanel>

        <InfoPanel>
          <Section id="contact-support">
            <SectionTitle>Contact Support</SectionTitle>
            <Text>
              If none of the solutions above resolved your technical issue, please email CAA RxDC Tech Support at
              {' '}
              <a href="mailto:caa_rxdc_tech_support@uhc.com">caa_rxdc_tech_support@uhc.com</a>
              .
              <br />
              <i>
                <b>Note:</b>
              </i>
              {' '}
              this email inbox is for RxDC UHC RFI technical issues ONLY. This inbox is monitored by the CAA RxDC
              Technical Support Team. During the UHC RFI data collection period, please allow up to two business days
              for a response. For non-technical issues or questions please reach out to your UHC Account
              Representative.
            </Text>
            <Text>
              For quicker resolution of technical issue, add the following to the email:
            </Text>
            <Text>
              <b>Subject:</b>
              {' '}
              CAA RxDC - UHC RFI Technical Issue
              <br />
              <b>Body:</b>
            </Text>
            <SectionList>
              <li><Text>Error screenshot</Text></li>
              <li><Text>Employer Group Name</Text></li>
              <li><Text>Policy Number(s)</Text></li>
              <li><Text>Describe Issue (Please be as detailed as possible!)</Text></li>
            </SectionList>
            <Text>Include the Employer/Broker Portal you signed in through to get to the UHC RFI.</Text>
            <Text>
              <b>Employer/Broker Portals:</b>
            </Text>
            <NumberList>
              <li><Text>Employer eServices</Text></li>
              <li><Text>UHCeServices</Text></li>
              <li><Text>Myallsavers (Employer)</Text></li>
              <li><Text>Myallsavers (Broker)</Text></li>
            </NumberList>
          </Section>
        </InfoPanel>

        <BackToTop />
      </Page>
    </React.Fragment>
  );
};
